import { format } from "date-fns"

import { Project } from "@/models/Project"
import { formatDate, formatDateAgo, getTodayCalendarDate, parseDate } from "@/utils/dateUtil"
import { toFixedNumber } from "@/utils/helpers"

import { Base } from "./Base"
import { Item } from "./Item"
import { Filter } from "./Pageable"
import { SearchProjectResult } from "./Search"
import { User } from "./User"

export const timesheetReportType = ["pdf", "xls"]

export const timesheetStatuses = ["NOT_STARTED", "DRAFT", "SUBMITTED", "DECLINED", "APPROVED"]

export const itemTimesheetTypes = [
  "WORK_DAY",
  "VACATION",
  "SICK_LEAVE",
  "HOLIDAYS",
  "COMPANY_ALL_DAY_EVENT",
  "MEETINGS",
  "TEAM_EVENTS",
  "TRAINING",
  "OTHER"
]

export const timesheetListOfDays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
]

export type TimesheetStatus = (typeof timesheetStatuses)[number]

export type ItemTimesheetType = (typeof itemTimesheetTypes)[number]

export type TimesheetReportType = (typeof timesheetReportType)[number]

export class CreateTimesheet {
  endDate: Date = getTodayCalendarDate()
  startDate: Date = getTodayCalendarDate()
  userId: string = ""

  constructor(props?: Partial<CreateTimesheet>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }

  getJsonObj() {
    return {
      endDate: format(this.endDate, "yyyy-MM-dd"),
      startDate: format(this.startDate, "yyyy-MM-dd"),
      userId: this.userId
    }
  }
}

export class TimesheetFilter extends Filter {
  endDate?: string
  startDate?: string
  status?: TimesheetStatus
  userId?: string

  constructor(props) {
    super(props)
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }

  getJsonObj() {
    const data = {
      endDate: this.endDate,
      startDate: this.startDate,
      status: this.status,
      userId: this.userId
    }
    return super.getJsonObj({
      params: data
    })
  }

  static create(props) {
    return new TimesheetFilter(props)
  }

  static createDefault() {
    const props = {}

    return new TimesheetFilter(props)
  }
}

export class ItemTimesheetWeeks {
  monday: null | number = null
  tuesday: null | number = null
  wednesday: null | number = null
  thursday: null | number = null
  friday: null | number = null
  saturday: null | number = null
  sunday: null | number = null
  total: null | number = null

  constructor(props?: Partial<ItemTimesheetWeeks>) {
    if (!props) return
    let total = 0
    Object.keys(props).forEach(field => {
      total += props[field] ? Number(props[field]) : 0
      this[field] = props[field] ? toFixedNumber(props[field]) : null
    })
    this.total = toFixedNumber(total)
  }

  getJsonObj() {
    return {
      monday: this.monday,
      tuesday: this.tuesday,
      wednesday: this.wednesday,
      thursday: this.thursday,
      friday: this.friday,
      saturday: this.saturday,
      sunday: this.sunday
    }
  }
}
export class ItemTimesheet extends Base<ItemTimesheet> {
  billable = false
  id = ""
  itemId = ""
  itemSnapshot?: Item
  projectId = ""
  type: ItemTimesheetType = "WORK_DAY"
  weekHours: ItemTimesheetWeeks = new ItemTimesheetWeeks()
  description = ""

  constructor(props?: Partial<ItemTimesheet>) {
    super()
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.itemSnapshot = props.itemSnapshot ? new Item(props.itemSnapshot) : undefined
    this.weekHours = new ItemTimesheetWeeks(props.weekHours)
  }

  get total(): number | null {
    let total = 0
    const filteredDays = timesheetListOfDays.filter(day => this.weekHours[day])
    if (filteredDays.length === 0) {
      return null
    }
    timesheetListOfDays
      .filter(day => this.weekHours[day])
      .forEach(day => {
        total += this.weekHours[day]
      })
    return total ? toFixedNumber(total) : null
  }

  isModified(keys?: (keyof ItemTimesheet)[], inverse?: boolean) {
    let isModified = false
    if (!isModified) {
      isModified = super.isModified(keys, inverse)
    }
    return isModified
  }

  get isWorkDay() {
    return this.type === "WORK_DAY"
  }

  get isVacation() {
    return this.type === "VACATION"
  }

  get isSickLeave() {
    return this.type === "SICK_LEAVE"
  }

  get isHolidays() {
    return this.type === "HOLIDAYS"
  }

  get isCompanyAllDayEvent() {
    return this.type === "COMPANY_ALL_DAY_EVENT"
  }

  get isMeetings() {
    return this.type === "MEETINGS"
  }

  get isTeamEvents() {
    return this.type === "TEAM_EVENTS"
  }

  get isTraining() {
    return this.type === "TRAINING"
  }

  get isOther() {
    return this.type === "OTHER"
  }

  get hideSelectProject() {
    return this.isVacation || this.isSickLeave || this.isHolidays || this.isCompanyAllDayEvent
  }

  getJsonObj() {
    return {
      billable: this.billable,
      id: this.id,
      itemId: this.itemId,
      itemSnapshot: this.itemSnapshot ? this.itemSnapshot.getJsonObj() : null,
      projectId: this.projectId,
      type: this.type,
      weekHours: this.weekHours.getJsonObj(),
      description: this.description
    }
  }
}

export class Timesheet extends Base<Timesheet> {
  id = ""
  endDate = ""
  startDate = ""
  entries: ItemTimesheet[] = []
  status: TimesheetStatus = "NOT_STARTED"
  approvedAt = ""
  approvedBy: User = User.createDefault()
  declineReason = ""
  declinedAt = ""
  declinedBy: User = User.createDefault()
  submittedAt = ""
  submittedBy: User = User.createDefault()
  updatedAt = ""
  updatedBy: User = User.createDefault()
  user: User = User.createDefault()
  weekHours: ItemTimesheetWeeks = new ItemTimesheetWeeks()
  billableHours: number = 0
  unBillableHours: number = 0
  totalHours: number = 0
  userProjects: SearchProjectResult[] = []
  modifiedDate = ""

  constructor(props?: Partial<Timesheet>) {
    super()
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    if (props.totalHours) {
      if (props.billableHours) {
        this.unBillableHours = props.totalHours - props.billableHours
      } else {
        this.unBillableHours = props.totalHours
      }
    }

    this.approvedBy = new User(this.approvedBy)
    this.declinedBy = new User(this.declinedBy)
    this.submittedBy = new User(this.submittedBy)
    this.updatedBy = new User(this.updatedBy)
    this.user = new User(this.user)
    this.weekHours = new ItemTimesheetWeeks(props.weekHours)
    this.entries = props.entries ? props.entries.map(entry => new ItemTimesheet(entry)) : []
    this.userProjects = props.userProjects
      ? props.userProjects.map(project => new SearchProjectResult(project))
      : []
    this.calculateMetrics()
  }

  get isSubmitted() {
    return this.status === "SUBMITTED"
  }

  get isDeclined() {
    return this.status === "DECLINED"
  }

  get isApproved() {
    return this.status === "APPROVED"
  }

  get isDraft() {
    return this.status === "DRAFT"
  }

  get isNotStarted() {
    return this.status === "NOT_STARTED"
  }

  get parseDate() {
    return (
      formatDate(this.startDate, "dd MMM", { dateOnly: true }) +
      " - " +
      formatDate(this.endDate, "dd MMM", { dateOnly: true })
    )
  }

  get parseApprovedAt() {
    return this.approvedAt ? formatDate(this.approvedAt, "hh:mm a MM/dd/yy") : ""
  }

  get parseDeclinedAt() {
    return this.declinedAt ? formatDate(this.declinedAt, "hh:mm a MM/dd/yy") : ""
  }

  get parseSubmittedAt() {
    return this.submittedAt ? formatDate(this.submittedAt, "hh:mm a MM/dd/yy") : ""
  }

  get parseUpdatedAt() {
    return this.updatedAt ? formatDate(this.updatedAt, "hh:mm a MM/dd/yy") : ""
  }

  get parseModifiedDate() {
    return this.modifiedDate ? formatDate(this.modifiedDate, "hh:mm a MM/dd/yy") : ""
  }

  get limitHours() {
    return 24
  }

  get exceedTimeOfDays() {
    return timesheetListOfDays.filter(day => {
      return this.weekHours[day] > this.limitHours
    })
  }

  checkExceedTimeOfDay(day: string, index: number) {
    if (!this.exceedTimeOfDays.includes(day) || !this.entries[index]?.weekHours?.[day]) {
      return false
    }
    const sliceList = this.entries.slice(0, index + 1)
    const sum = sliceList.reduce((acc, item) => acc + item.weekHours[day], 0)
    return sum > 24
  }

  calculateMetrics(projectId?: string) {
    this.weekHours = new ItemTimesheetWeeks()
    this.totalHours = 0
    this.billableHours = 0
    this.unBillableHours = 0

    const filteredEntries = projectId
      ? this.entries.filter(item => item.projectId === projectId || !item.projectId)
      : this.entries

    filteredEntries.forEach(timesheet => {
      timesheetListOfDays.forEach(day => {
        this.weekHours[day] = toFixedNumber(this.weekHours[day] + timesheet.weekHours[day])
      })
      const total = timesheet.total ?? 0
      this.totalHours = toFixedNumber(this.totalHours + total)
      if (timesheet.billable) {
        this.billableHours = toFixedNumber(this.billableHours + total)
      } else {
        this.unBillableHours = toFixedNumber(this.unBillableHours + total)
      }
    })
  }

  static addTimes(time1: string, time2: string) {
    return (Number(time1) + Number(time2)).toString()
  }

  isModified(keys?: (keyof Timesheet)[], inverse?: boolean) {
    let isModified = false
    if (!isModified) {
      isModified = super.isModified(keys, inverse)
    }
    return isModified
  }

  getJsonObj() {
    return {
      id: this.id,
      endDate: this.endDate,
      startDate: this.startDate,
      entries: this.entries.map(entry => entry.getJsonObj()),
      status: this.status,
      approvedAt: this.approvedAt,
      approvedBy: this.approvedBy.getJsonObj(),
      declineReason: this.declineReason,
      declinedAt: this.declinedAt,
      declinedBy: this.declinedBy.getJsonObj(),
      submittedAt: this.submittedAt,
      submittedBy: this.submittedBy.getJsonObj(),
      updatedAt: this.updatedAt,
      updatedBy: this.updatedBy.getJsonObj(),
      user: this.user.getJsonObj(),
      billableHours: Number(this.billableHours),
      unBillableHours: Number(this.unBillableHours),
      totalHours: Number(this.billableHours)
    }
  }
}

export class TimesheetSimplify {
  id = ""
  endDate = ""
  startDate = ""
  status: TimesheetStatus = "NOT_STARTED"
  user: User = User.createDefault()
  updatedBy: User = User.createDefault()
  totalWeekHours: ItemTimesheetWeeks = new ItemTimesheetWeeks()
  modifiedDate = ""
  billableHours: number | null = null
  unBillableHours: number | null = null
  totalHours: number | null = null
  declineReason = ""
  updatedAt = ""

  constructor(props?: Partial<TimesheetSimplify>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.totalWeekHours = new ItemTimesheetWeeks(props.totalWeekHours)
    this.user = new User(props.user)
    this.updatedBy = new User(props.updatedBy)
    if (this.totalHours) {
      const unBillableHours = this.billableHours
        ? this.totalHours - this.billableHours
        : this.totalHours
      this.unBillableHours = toFixedNumber(unBillableHours)
    }
  }

  get isSubmitted() {
    return this.status === "SUBMITTED"
  }

  get isDeclined() {
    return this.status === "DECLINED"
  }

  get isApproved() {
    return this.status === "APPROVED"
  }

  get isDraft() {
    return this.status === "DRAFT"
  }

  get isNotStarted() {
    return this.status === "NOT_STARTED"
  }

  get parseModifiedDate() {
    return this.modifiedDate ? formatDate(this.modifiedDate, "hh:mm a MM/dd/yy") : ""
  }

  get parseUpdatedAt() {
    return this.updatedAt ? formatDate(this.updatedAt, "hh:mm a MM/dd/yy") : ""
  }

  get parseDate() {
    return (
      formatDate(this.startDate, "dd MMM", { dateOnly: true }) +
      " - " +
      formatDate(this.endDate, "dd MMM", { dateOnly: true })
    )
  }

  get isCurrentWeek() {
    const currentDate = getTodayCalendarDate()
    const currentDayOfWeek = currentDate.getDay()
    const startOfWeek = new Date(currentDate)
    const endOfWeek = new Date(currentDate)

    startOfWeek.setDate(
      currentDate.getDate() - currentDayOfWeek + (currentDayOfWeek === 0 ? -6 : 1)
    )
    startOfWeek.setHours(0, 0, 0, 0)

    endOfWeek.setDate(currentDate.getDate() - currentDayOfWeek + (currentDayOfWeek === 0 ? 0 : 7))
    endOfWeek.setHours(23, 59, 59, 999)

    const startDate = parseDate(this.startDate)
    const endDate = parseDate(this.endDate)

    return startDate >= startOfWeek && endDate <= endOfWeek
  }
}

export class TimesheetStats {
  statusCounts: { [key: string]: number } = {}
  total = 0

  constructor(props?: Partial<TimesheetStats>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.statusCounts.total = props.total ? props.total : 0
  }
}

export interface WeekInterval {
  from: string
  to: string
  displayData?: string
  closed?: boolean
}

export interface MonthInterval {
  month: string
  displayData?: string
}

export class MonthIntervals {
  months: string[]

  constructor(data: string[]) {
    this.months = data
  }

  get monthsDisplayData(): MonthInterval[] {
    return this.months.map(month => {
      return {
        month: month,
        displayData: formatDate(month, "MMM yyyy", { dateOnly: true })
      }
    })
  }
}

export class WeeksIntervals {
  intervals: WeekInterval[]

  constructor(data: WeekInterval[]) {
    this.intervals = data.map(interval => ({
      ...interval,
      displayData: this.formatDisplayData(interval.from, interval.to)
    }))
  }

  private formatDisplayData(from: string, to: string): string {
    return `${formatDate(from, "dd MMM", { dateOnly: true })} - ${formatDate(to, "dd MMM", { dateOnly: true })}`
  }
}

export class TimesheetSummary {
  project: Project = new Project()
  approved = 0
  outstanding = 0
  teamMembers = 0
  from = ""
  to = ""
  projectId = ""
  billableHours = 0
  totalHours = 0

  constructor(props?: Partial<TimesheetSummary>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.project = new Project(props.project)
  }
}

export class TimesheetSummaryWeeks {
  billableHours: number = 0
  completed: boolean = false
  hoursByWeek: HoursByWeek[] = []
  projectId: string = ""
  totalHours: number = 0
  user: User = User.createDefault()

  constructor(props: TimesheetSummaryWeeks) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.user = new User(props.user)
  }
}

export interface TimesheetSummaryDowlandParams {
  month: string
  project: Project
}

export interface HoursByWeek {
  from: string
  to: string
  hours: number
  id: string
  status: TimesheetStatus
}

export interface TimesheetChangeHistoryHours {
  after: number
  before: number
  changed: boolean
}

export interface TimesheetChangeHistoryReason {
  after: string
  before: string
  changed: boolean
}

export class TimesheetChangeHistoryEntries {
  after?: ItemTimesheet
  before?: ItemTimesheet
  changed = false

  constructor(props?: Partial<TimesheetChangeHistoryEntries>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.after = props.after ? new ItemTimesheet(props.after) : undefined
    this.before = props.before ? new ItemTimesheet(props.before) : undefined
  }
}

export interface TimesheetChangeHistoryStatus {
  after: TimesheetStatus
  before: TimesheetStatus
  changed: boolean
}

export class TimesheetAuditRecord {
  billableHours?: TimesheetChangeHistoryHours
  declineReason?: TimesheetChangeHistoryReason
  entries: TimesheetChangeHistoryEntries[] = []
  status?: TimesheetChangeHistoryStatus
  totalHours?: TimesheetChangeHistoryHours

  constructor(props?: Partial<TimesheetAuditRecord>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.entries = props.entries
      ? props.entries.map(entry => new TimesheetChangeHistoryEntries(entry))
      : []
  }

  get unbillableHours(): TimesheetChangeHistoryHours | undefined {
    if (this.totalHours && this.billableHours) {
      const after = this.totalHours.after - this.billableHours.after
      const before = this.totalHours.before - this.billableHours.before
      if (after !== before) {
        return {
          after,
          before,
          changed: true
        }
      }
    }
    return undefined
  }
}

export class TimesheetChangeHistory {
  id = ""
  initiator: User = User.createDefault()
  state: TimesheetAuditRecord = new TimesheetAuditRecord()
  createdDate = ""

  constructor(props?: Partial<TimesheetChangeHistory>) {
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
    this.initiator = new User(props.initiator)
    this.state = new TimesheetAuditRecord(props.state)
  }

  get timeAge() {
    return formatDateAgo({
      date: new Date(),
      dateFormat: parseDate(this.createdDate, true),
      sufixAgo: true
    })
  }

  get parseCreatedDate() {
    return this.createdDate ? formatDate(this.createdDate, "HH:mm MM/dd/yy") : ""
  }

  get dayAndMonth() {
    const format =
      formatDate(this.createdDate, "yyyy") !== formatDate(new Date(), "yyyy")
        ? "MMMM dd, yyyy"
        : "MMMM dd"
    return formatDate(this.createdDate, format)
  }
}

export class TimesheetChangeHistoryFilter extends Filter {
  timesheetId = ""

  constructor(props?: Partial<TimesheetChangeHistoryFilter>) {
    super(props as TimesheetChangeHistoryFilter)
    if (!props) return
    Object.keys(props).forEach(field => {
      this[field] = props[field]
    })
  }

  getJsonObj() {
    return super.getJsonObj()
  }
}

export interface TimesheetWeeksDTO {
  start: string
  end: string
}

export class TimesheetWeeks {
  start: Date = getTodayCalendarDate()
  end: Date = getTodayCalendarDate()
  displayDate: string = ""

  constructor(props?: Partial<TimesheetWeeksDTO>) {
    if (!props) return
    this.end = props.end ? parseDate(props.end) : getTodayCalendarDate()
    this.start = props.start ? parseDate(props.start) : getTodayCalendarDate()
    this.displayDate =
      formatDate(this.start, "dd MMM", { dateOnly: true }) +
      " - " +
      formatDate(this.end, "dd MMM", { dateOnly: true })
  }
}
